import React, { useEffect, useState } from "react";
import { ContactSubmission } from "../../types/admin";
import AdminNav from "../../components/AdminNav";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";

const AdminContacts: React.FC = () => {
  const [contacts, setContacts] = useState<ContactSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] =
    useState<ContactSubmission | null>(null);

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(statusFilter && { status: statusFilter }),
      });

      const response = await fetch(
        `${API_BASE_URL}/api/admin/contacts?${params}`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("お問い合わせの取得に失敗しました");
      }

      const data = await response.json();
      if (data.success) {
        setContacts(data.data.submissions);
        setTotal(data.data.total);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "エラーが発生しました");
    } finally {
      setLoading(false);
    }
  };

  const updateContact = async (
    contactId: number,
    status: "new" | "in_progress" | "resolved",
    adminNotes?: string
  ) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE_URL}/api/admin/contacts/${contactId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status, admin_notes: adminNotes }),
        }
      );

      if (!response.ok) {
        throw new Error("更新に失敗しました");
      }

      alert("お問い合わせを更新しました");
      fetchContacts();
      setSelectedContact(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : "エラーが発生しました");
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && contacts.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <>
      <AdminNav />
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">お問い合わせ管理</h1>
          <div className="text-sm text-gray-600">総件数: {total}</div>
        </div>

        {/* フィルター */}
        <div className="mb-6 flex gap-3">
          <button
            onClick={() => {
              setStatusFilter("");
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg ${
              statusFilter === ""
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            すべて
          </button>
          <button
            onClick={() => {
              setStatusFilter("new");
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg ${
              statusFilter === "new"
                ? "bg-red-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            新規
          </button>
          <button
            onClick={() => {
              setStatusFilter("in_progress");
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg ${
              statusFilter === "in_progress"
                ? "bg-yellow-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            対応中
          </button>
          <button
            onClick={() => {
              setStatusFilter("resolved");
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg ${
              statusFilter === "resolved"
                ? "bg-green-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            解決済み
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* お問い合わせリスト */}
        <div className="space-y-4">
          {contacts.map((contact) => (
            <div
              key={contact.id}
              className="bg-white shadow-md rounded-lg p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-lg font-semibold">{contact.name}</h3>
                  <p className="text-sm text-gray-600">{contact.email}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      contact.status === "new"
                        ? "bg-red-100 text-red-800"
                        : contact.status === "in_progress"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-green-100 text-green-800"
                    }`}
                  >
                    {contact.status === "new"
                      ? "新規"
                      : contact.status === "in_progress"
                        ? "対応中"
                        : "解決済み"}
                  </span>
                  <button
                    onClick={() => setSelectedContact(contact)}
                    className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                  >
                    詳細・編集
                  </button>
                </div>
              </div>
              <div className="mb-3">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {contact.message}
                </p>
              </div>
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>
                  受信日時:{" "}
                  {new Date(contact.created_at).toLocaleString("ja-JP")}
                </span>
                {contact.resolved_at && (
                  <span>
                    解決日時:{" "}
                    {new Date(contact.resolved_at).toLocaleString("ja-JP")}
                  </span>
                )}
              </div>
              {contact.admin_notes && (
                <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs font-medium text-blue-900 mb-1">
                    管理者メモ:
                  </p>
                  <p className="text-sm text-blue-800">{contact.admin_notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ページネーション */}
        <div className="mt-6 flex justify-between items-center">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700"
          >
            前へ
          </button>
          <span className="text-sm text-gray-600">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700"
          >
            次へ
          </button>
        </div>

        {/* 詳細・編集モーダル */}
        {selectedContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4">お問い合わせ詳細</h2>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700">お名前:</p>
                <p className="text-base">{selectedContact.name}</p>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700">メール:</p>
                <p className="text-base">{selectedContact.email}</p>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700">受信日時:</p>
                <p className="text-base">
                  {new Date(selectedContact.created_at).toLocaleString("ja-JP")}
                </p>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700">メッセージ:</p>
                <div className="mt-1 p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-base whitespace-pre-wrap">
                    {selectedContact.message}
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ステータス
                </label>
                <select
                  value={selectedContact.status}
                  onChange={(e) =>
                    setSelectedContact({
                      ...selectedContact,
                      status: e.target.value as
                        | "new"
                        | "in_progress"
                        | "resolved",
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="new">新規</option>
                  <option value="in_progress">対応中</option>
                  <option value="resolved">解決済み</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  管理者メモ
                </label>
                <textarea
                  value={selectedContact.admin_notes || ""}
                  onChange={(e) =>
                    setSelectedContact({
                      ...selectedContact,
                      admin_notes: e.target.value,
                    })
                  }
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="対応内容やメモを入力..."
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setSelectedContact(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  キャンセル
                </button>
                <button
                  onClick={() =>
                    updateContact(
                      selectedContact.id,
                      selectedContact.status,
                      selectedContact.admin_notes || undefined
                    )
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminContacts;
